import React from "react";

const joinParts = (lineId, parts) =>
  parts.filter(Boolean).reduce((acc, part, idx) => {
    if (idx > 0) {
      // On regarde la clé du composant React pour savoir quel séparateur utiliser
      const prevPart = parts.filter(Boolean)[idx - 1];
      const currentPart = part;
      
      let sep = ", ";
      
      // Si la partie précédente est la dose ou la durée, on utilise un espace au lieu d'une virgule
      if (prevPart.key === "dose" || prevPart.key === "dur") {
        sep = " ";
      }

      acc.push(
        <span key={`sep-${lineId}-${idx}`} className="np-sep">
          {sep}
        </span>,
      );
    }
    acc.push(part);
    return acc;
  }, []);

const formatQspDisplay = (quantity, quantityType) => {
  const qty = String(quantity ?? "").trim();
  const type = String(quantityType ?? "").trim();
  if (!qty) return "";

  const normalized = type.toLowerCase();
  const isMonths = ["qspm", "qsp_months", "qsp_mois"].includes(normalized);
  const isDays = ["qspj", "qsp_days", "qsp_jours"].includes(normalized);

  if (isMonths) return `QSP ${qty} mois`;
  if (isDays) return `QSP ${qty} jours`;
  return `${qty}${type ? ` ${type}` : ""}`;
};

const renderSelectableLine = (line, ctx, content) => {
  const {
    onSelectLine,
    onDeleteLine,
    selectedLineId,
    selectedBlockId,
    blockId,
  } = ctx;
  const isActive = selectedLineId === line.id && selectedBlockId === blockId;

  return (
    <div
      className={isActive ? "np-line-row active" : "np-line-row"}
      key={line.id}
    >
      <button
        type="button"
        className={
          isActive
            ? "np-line np-line-selectable active np-line-trigger"
            : "np-line np-line-selectable np-line-trigger"
        }
        onClick={() => onSelectLine(blockId, line.id)}
      >
        {content}
      </button>
      {typeof onDeleteLine === "function" && line.type !== "condition_line" ? (
        <button
          type="button"
          className="np-line-delete"
          aria-label="Delete line"
          title="Delete line"
          onClick={(event) => {
            event.stopPropagation();
            onDeleteLine(blockId, line.id);
          }}
        >
          ×
        </button>
      ) : null}
    </div>
  );
};

const renderDosageLine = (line, ctx = {}) => {
  const { onSelectLine, selectedLineId, selectedBlockId, blockId } = ctx;
  const canSelect = typeof onSelectLine === "function";
  const durationPart =
    line.duree_unite === "calendar"
      ? (() => {
          const start = (line.duree_calendar_start || "").trim();
          const end = (line.duree_calendar_end || "").trim();
          if (!start && !end) return null;
          if (start && end) return `j${start} à j${end}`;
          return start ? `j${start}` : `j${end}`;
        })()
      : line.duree_valeur
        ? `${line.duree_valeur} ${line.duree_unite || ""}`
        : null;

  const parts = [
    line.dose ? (
      <span
        key="dose"
        className="np-text-red"
      >{`${line.dose} ${line.unite || ""}`}</span>
    ) : null,
    line.frequence ? (
      <span key="freq" className="np-text-red">
        {line.frequence}
      </span>
    ) : null,
    line.voie ? (
      <span key="voie" className="np-text-strong">
        {line.voie}
      </span>
    ) : null,
    line.moment ? (
      <span key="moment" className="np-text-soft">
        {line.moment}
      </span>
    ) : null,
    line.repas ? (
      <span key="repas" className="np-text-soft">
        {line.repas}
      </span>
    ) : null,
    durationPart ? (
      <span key="dur" className="np-text-red">
        {durationPart}
      </span>
    ) : null,
    line.condition ? (
      <span key="cond" className="np-text-red">
        {(() => {
          const cond = String(line.condition).trim();
          return cond.toLowerCase().startsWith("si ") ? cond : `si ${cond}`;
        })()}
      </span>
    ) : null,
    line.renouvellement ? (
      <span key="renew" className="np-text-soft">
        {line.renouvellement}
      </span>
    ) : null,
  ].filter(Boolean);

  const content = joinParts(line.id, parts);

  if (!canSelect) {
    return (
      <div className="np-line" key={line.id}>
        {content}
      </div>
    );
  }

  return renderSelectableLine(line, ctx, content);
};

const renderStepLine = (line, ctx = {}) => {
  const { onSelectLine } = ctx;
  const canSelect = typeof onSelectLine === "function";
  const legacyStep = (line.steps || [])[0] || {};
  const source = {
    dose: line.dose ?? legacyStep.dose,
    unite: line.unite ?? legacyStep.unite,
    frequence: line.frequence ?? legacyStep.frequence,
    voie: line.voie ?? legacyStep.voie,
    moment: line.moment ?? legacyStep.moment,
    repas: line.repas ?? legacyStep.repas,
    duree_valeur: line.duree_valeur ?? legacyStep.duree_valeur,
    duree_unite: line.duree_unite ?? legacyStep.duree_unite,
    duree_calendar_start:
      line.duree_calendar_start ?? legacyStep.duree_calendar_start,
    duree_calendar_end:
      line.duree_calendar_end ?? legacyStep.duree_calendar_end,
    condition: line.condition ?? legacyStep.condition,
    renouvellement: line.renouvellement ?? legacyStep.renouvellement,
  };

  const duration =
    source.duree_unite === "calendar"
      ? (() => {
          const start = (source.duree_calendar_start || "").trim();
          const end = (source.duree_calendar_end || "").trim();
          if (!start && !end) return null;
          if (start && end) return `j${start} à j${end}`;
          return start ? `j${start}` : `j${end}`;
        })()
      : source.duree_valeur
        ? `${source.duree_valeur} ${source.duree_unite || ""}`
        : null;

  const parts = [
    source.dose ? (
      <span
        key="dose"
        className="np-text-red"
      >{`${source.dose} ${source.unite || ""}`}</span>
    ) : null,
    source.frequence ? (
      <span key="freq" className="np-text-red">
        {source.frequence}
      </span>
    ) : null,
    source.voie ? (
      <span key="voie" className="np-text-strong">
        {source.voie}
      </span>
    ) : null,
    source.moment ? (
      <span key="moment" className="np-text-soft">
        {source.moment}
      </span>
    ) : null,
    source.repas ? (
      <span key="repas" className="np-text-soft">
        {source.repas}
      </span>
    ) : null,
    duration ? (
      <span key="dur" className="np-text-red">
        {duration}
      </span>
    ) : null,
    source.condition ? (
      <span key="cond" className="np-text-red">
        {(() => {
          const cond = String(source.condition).trim();
          return cond.toLowerCase().startsWith("si ") ? cond : `si ${cond}`;
        })()}
      </span>
    ) : null,
    source.renouvellement ? (
      <span key="renew" className="np-text-soft">
        {source.renouvellement}
      </span>
    ) : null,
  ];

  const content = (
    <>
      <span className="np-text-strong">puis</span>
      <span className="np-sep"> </span>
      {joinParts(line.id, parts)}
    </>
  );

  if (!canSelect) {
    return (
      <div className="np-line" key={line.id}>
        {content}
      </div>
    );
  }

  return renderSelectableLine(line, ctx, content);
};

const renderConditionLine = (line, ctx = {}) => {
  const { onSelectLine } = ctx;
  const canSelect = typeof onSelectLine === "function";
  const actionLabel = (() => {
    switch (line.action_type) {
      case "association":
        return "en association avec";
      case "relay":
        return "relai par";
      case "create_block":
        return "";
      case "add_dosage_line":
        return "";
      case "none":
        return "";
      default:
        return line.action_type || "action";
    }
  })();

  const hasExpression = Boolean((line.expression || "").trim());

  const content = (
    <>
      {hasExpression ? (
        <span className="np-text-red">{line.expression}</span>
      ) : null}
      {hasExpression && actionLabel ? <span className="np-sep">, </span> : null}
      {actionLabel ? (
        <span className="np-text-strong">{actionLabel}</span>
      ) : null}
      {actionLabel && line.action_medication ? (
        <span className="np-text-soft"> : {line.action_medication}</span>
      ) : null}
    </>
  );

  if (!canSelect) {
    return (
      <div className="np-line" key={line.id}>
        {content}
      </div>
    );
  }

  return renderSelectableLine(line, ctx, content);
};

const renderTestLine = (line, ctx = {}) => {
  const { onSelectLine } = ctx;
  const canSelect = typeof onSelectLine === "function";
  const tests = line.tests || [];
  const content = (
    <>
      <div className="np-text-strong">Tests</div>
      <div className="np-test-list">
        {tests.length ? (
          tests.map((test) => (
            <div key={test.id} className="np-text-soft">{`- ${test.name}`}</div>
          ))
        ) : (
          <div className="np-text-soft">No tests selected</div>
        )}
      </div>
    </>
  );

  if (!canSelect) {
    return (
      <div className="np-line" key={line.id}>
        {content}
      </div>
    );
  }

  return renderSelectableLine(line, ctx, content);
};

const renderLine = (line, ctx) => {
  if (line.type === "step_line") return renderStepLine(line, ctx);
  if (line.type === "condition_line") return renderConditionLine(line, ctx);
  if (line.type === "test_line") return renderTestLine(line, ctx);
  return renderDosageLine(line, ctx);
};

const renderAssocChild = (block, ctx) => (
  <div className="np-assoc-item" key={block.id}>
    <div className="np-med-row">
      <span
        className={
          typeof ctx.onSelectBlock === "function"
            ? "np-med-name np-med-child np-med-name-clickable"
            : "np-med-name np-med-child"
        }
        onClick={
          typeof ctx.onSelectBlock === "function"
            ? () => ctx.onSelectBlock(block.id)
            : undefined
        }
        role={typeof ctx.onSelectBlock === "function" ? "button" : undefined}
        tabIndex={typeof ctx.onSelectBlock === "function" ? 0 : undefined}
        onKeyDown={
          typeof ctx.onSelectBlock === "function"
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  ctx.onSelectBlock(block.id);
                }
              }
            : undefined
        }
      >
        {(() => {
          const rawName = ctx.showShortName
            ? block.medicament?.shortName ||
              block.medicament?.name ||
              (ctx.allowUntitled ? "Untitled medication" : "")
            : block.medicament?.name ||
              (ctx.allowUntitled ? "Untitled medication" : "");
          return String(rawName).replace(/\s*-\s*/g, " ");
        })()}
      </span>
      {block.medicament?.molecules && block.medicament.molecules.length > 0 && (
           <div className="np-med-molecules" style={{ fontSize: '11px', color: '#777', fontStyle: 'italic', marginTop: '2px' }}>
             {block.medicament.molecules.join(', ')}
           </div>
         )}
         {block.quantite ? (
        <span className="np-qsp">
          {formatQspDisplay(block.quantite, block.type_quantite)}
        </span>
      ) : null}
    </div>
    {(block.lines || []).map((line) =>
      renderLine(line, { ...ctx, blockId: block.id }),
    )}
    {block.children && block.children.length > 0 ? (
      <div className="np-assoc-list">
        {block.children.map((child) => renderAssocChild(child, ctx))}
      </div>
    ) : null}
  </div>
);

const renderBlockPreview = (block, ctx, entryNote) => {
  return (
    <div className="np-preview-card" key={block.id}>
      <div className="np-med-row">
        <span
          className={
            typeof ctx.onSelectBlock === "function"
              ? "np-med-name np-med-name-clickable"
              : "np-med-name"
          }
          onClick={
            typeof ctx.onSelectBlock === "function"
              ? () => ctx.onSelectBlock(block.id)
              : undefined
          }
          role={typeof ctx.onSelectBlock === "function" ? "button" : undefined}
          tabIndex={typeof ctx.onSelectBlock === "function" ? 0 : undefined}
          onKeyDown={
            typeof ctx.onSelectBlock === "function"
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    ctx.onSelectBlock(block.id);
                  }
                }
              : undefined
          }
        >
          {(() => {
            const rawName = ctx.showShortName
              ? block.medicament?.shortName ||
                block.medicament?.name ||
                (ctx.allowUntitled ? "Untitled medication" : "")
              : block.medicament?.name ||
                (ctx.allowUntitled ? "Untitled medication" : "");
            return String(rawName).replace(/\s*-\s*/g, " ");
          })()}
        </span>
        {block.quantite ? (
          <span className="np-qsp">
            {formatQspDisplay(block.quantite, block.type_quantite)}
          </span>
        ) : null}
      </div>

      {(block.lines || []).map((line) =>
        renderLine(line, { ...ctx, blockId: block.id }),
      )}

      {block.children && block.children.length > 0 ? (
        <div className="np-assoc">
          <div className="np-assoc-list">
            {block.children.map((child) => renderAssocChild(child, ctx))}
          </div>
        </div>
      ) : null}

      {entryNote ? (
        <div
          className="np-text-soft"
          style={{ textAlign: "left", marginTop: 8 }}
        >
          {entryNote}
        </div>
      ) : null}
    </div>
  );
};

export const PreviewBlocks = ({
  blocks,
  entryNote,
  selectedBlockId,
  selectedLineId,
  onSelectLine,
  onSelectBlock,
  onDeleteLine,
  showShortName = false,
  allowUntitled = false,
}) => {
  if (!blocks || !blocks.length)
    return <div className="np-warning">No lines yet.</div>;
  return (
    <>
      {blocks.map((block) =>
        renderBlockPreview(
          block,
          {
            selectedBlockId,
            selectedLineId,
            onSelectLine,
            onSelectBlock,
            onDeleteLine,
            showShortName,
            allowUntitled,
          },
          entryNote,
        ),
      )}
    </>
  );
};

export default PreviewBlocks;
